import type { LinkedUnassociatedMail, UnassociatedMailCount, UnassociatedMailPage } from '@easyhelp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, type ApiError } from './client';
import { ticketsQueryKey } from './tickets';

/** Radice delle chiavi della posta non associata (#42): lista e contatore si riallineano insieme. */
export const unassociatedMailQueryKey = ['unassociated-mail'] as const;
const listKey = [...unassociatedMailQueryKey, 'list'] as const;
const countKey = [...unassociatedMailQueryKey, 'count'] as const;

/** Ogni minuto lista e contatore si riallineano: le email arrivano da sole. */
const REFRESH_MS = 60_000;

/** `GET /unassociated-mail`: le email in attesa, dalla più recente, e il totale. */
export function useUnassociatedMail() {
  return useQuery({
    queryKey: listKey,
    queryFn: () => apiRequest<UnassociatedMailPage>('/unassociated-mail'),
    refetchInterval: REFRESH_MS,
  });
}

/** `GET /unassociated-mail/count`: il contatore della voce di navigazione. */
export function useUnassociatedMailCount() {
  return useQuery({
    queryKey: countKey,
    queryFn: () => apiRequest<UnassociatedMailCount>('/unassociated-mail/count'),
    refetchInterval: REFRESH_MS,
  });
}

/**
 * `POST /unassociated-mail/:id/link`: collega l'email a un ticket esistente. Dopo il collegamento si
 * riallineano la posta non associata e i ticket (il thread ha un messaggio in più).
 */
export function useLinkUnassociatedMail() {
  const queryClient = useQueryClient();
  return useMutation<LinkedUnassociatedMail, ApiError, { id: string; ticketCode: string }>({
    mutationFn: ({ id, ticketCode }) =>
      apiRequest<LinkedUnassociatedMail>(`/unassociated-mail/${encodeURIComponent(id)}/link`, {
        method: 'POST',
        body: JSON.stringify({ ticketCode }),
      }),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: unassociatedMailQueryKey }),
        queryClient.invalidateQueries({ queryKey: ticketsQueryKey }),
      ]),
  });
}

/** `POST /unassociated-mail/:id/discard`: scarta l'email (il testo è cancellato, resta il registro). */
export function useDiscardUnassociatedMail() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => apiRequest<void>(`/unassociated-mail/${encodeURIComponent(id)}/discard`, { method: 'POST' }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: unassociatedMailQueryKey }),
  });
}
